import React from 'react';
import { Collapse, Typography, Row, Col, CollapseProps } from 'antd';
import { FormFieldRenderer } from './form-field-renderer';
import { MultiFieldProps } from '../../types/form-renderer';
import type { FormField } from '../../types/templates';
import { AccordionConfig } from '../../types/accordion';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { useTemplateConfig } from '../../hooks/banners/use-template-config';

const { Text } = Typography;

export const AccordionFormRenderer: React.FC<MultiFieldProps> = ({
  fields,
  formData,
  onFieldChange,
  errors,
  creativityType
}) => {
  const template = useTemplateConfig(creativityType);
  const accordionConfig = template?.renderConfig?.accordionConfig;

  // Field map
  const fieldMap = React.useMemo(() => {
    const map: Record<string, FormField> = {};
    fields.forEach(field => {
      map[field.key] = field;
    });
    return map;
  }, [fields]);

  const { fontSize } = useResponsiveSizes();

  const defaultActiveKeys =
    accordionConfig
      ?.filter((config: AccordionConfig) => config.defaultOpen)
      .map((config: AccordionConfig) => config.key) || [];

  if (!accordionConfig) {
    return (
      <div style={{ marginTop: 32, width: '100%' }}>
        {fields.map(field => (
          <FormFieldRenderer
            key={field.key}
            field={field}
            formData={formData}
            onFieldChange={onFieldChange}
            errors={errors}
            creativityType={creativityType}
          />
        ))}
      </div>
    );
  }

  const renderRegionGrid = (config: AccordionConfig) => {
    const regionFields = config.fields.filter((field: string) => field.startsWith('region'));
    const regionNumbers: number[] = regionFields.map((field: string) => parseInt(field.replace('region', '')));

    return (
      <Row gutter={[24, 16]}>
        {/* Switch de sincronización */}
        <Col xs={24} lg={24}>
          <FormFieldRenderer
            field={fieldMap['autoSyncTimes']}
            formData={formData}
            onFieldChange={onFieldChange}
            errors={errors}
            creativityType={creativityType}
            customStyles={{ marginBottom: 0 }}
          />
        </Col>
        <Col span={0}></Col>

        {/* Dynamically generated regions */}
        {regionNumbers.map(i => {
          const regionField = fieldMap[`region${i}`];
          const timeField = fieldMap[`time${i}`];
          const enableField = fieldMap[`enableRegion${i}`];

          if (!regionField || !timeField) return null;

          return (
            <Col key={i} span={12}>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}
              >
                {/* Título con switch integrado */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}
                >
                  <Text>{i}</Text>
                  {enableField && (
                    <FormFieldRenderer
                      field={enableField}
                      formData={formData}
                      onFieldChange={onFieldChange}
                      errors={errors}
                      creativityType={creativityType}
                      customStyles={{ justifyContent: 'end', marginBottom: 0 }}
                    />
                  )}
                </div>

                <FormFieldRenderer
                  field={regionField}
                  formData={formData}
                  onFieldChange={onFieldChange}
                  errors={errors}
                  creativityType={creativityType}
                />
                <div style={{ marginTop: '12px' }}>
                  <FormFieldRenderer
                    field={timeField}
                    formData={formData}
                    onFieldChange={onFieldChange}
                    errors={errors}
                    creativityType={creativityType}
                  />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderNormalLayout = (groupFields: FormField[]) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {groupFields.map(field => (
        <div key={field.key} style={{ width: '100%' }}>
          <FormFieldRenderer
            field={field}
            formData={formData}
            onFieldChange={onFieldChange}
            errors={errors}
            creativityType={creativityType}
          />
        </div>
      ))}
    </div>
  );

  const items: CollapseProps['items'] = accordionConfig.flatMap((config: AccordionConfig) => {
    const groupFields: FormField[] = config.fields.map(fieldKey => fieldMap[fieldKey]).filter(Boolean);

    if (groupFields.length === 0) return [];

    return [
      {
        key: config.key,
        header: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '4px',
                height: '20px',
                backgroundColor: config.color,
                borderRadius: '2px'
              }}
            />
            <Text style={{ fontSize: fontSize.lg, color: '#262626' }}>{config.title}</Text>
          </div>
        ),
        style: {
          marginBottom: '16px',
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #f0f0f0'
        },
        children:
          config.layout === 'grid' && config.key === 'regions'
            ? renderRegionGrid(config)
            : renderNormalLayout(groupFields)
      }
    ];
  });

  return (
    <div style={{ marginTop: 32, width: '100%' }}>
      <Collapse defaultActiveKey={defaultActiveKeys} style={{ backgroundColor: 'transparent' }} ghost items={items} />
    </div>
  );
};
